(() => {
  if (window.__customFullscreenHeroV2Loaded) return;
  window.__customFullscreenHeroV2Loaded = true;

  const selector = '[data-cfhv2]';
  const instances = new Map();
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class HeroSliderV2 {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      this.slides = [...root.querySelectorAll('[data-cfhv2-slide]')];
      this.dots = [...root.querySelectorAll('[data-cfhv2-dot]')];
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.timer = null;
      this.locked = false;
      this.visible = true;
      this.autoplay = root.dataset.autoplay === 'true' && !reduceMotion();
      this.loop = root.dataset.loop !== 'false';
      this.pauseOnHover = root.dataset.pauseHover === 'true';
      this.interval = Math.max(2000, Number(root.dataset.interval) || 5000);
      this.init();
    }

    init() {
      if (this.slides.length < 2) return;
      this.root.querySelector('[data-cfhv2-prev]')?.addEventListener('click', () => this.go(this.index - 1, 'prev', true), { signal: this.abort.signal });
      this.root.querySelector('[data-cfhv2-next]')?.addEventListener('click', () => this.go(this.index + 1, 'next', true), { signal: this.abort.signal });
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.cfhv2Dot), Number(dot.dataset.cfhv2Dot) < this.index ? 'prev' : 'next', true), { signal: this.abort.signal }));

      this.root.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.go(this.index - 1, 'prev', true);
        if (event.key === 'ArrowRight') this.go(this.index + 1, 'next', true);
      }, { signal: this.abort.signal });

      let startX = 0;
      let startY = 0;
      this.root.addEventListener('touchstart', (event) => {
        startX = event.changedTouches[0]?.clientX || 0;
        startY = event.changedTouches[0]?.clientY || 0;
      }, { passive: true, signal: this.abort.signal });
      this.root.addEventListener('touchend', (event) => {
        const touch = event.changedTouches[0];
        if (!touch) return;
        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.2) this.go(this.index + (dx < 0 ? 1 : -1), dx < 0 ? 'next' : 'prev', true);
      }, { passive: true, signal: this.abort.signal });

      if (this.pauseOnHover) {
        this.root.addEventListener('mouseenter', () => this.stop(), { signal: this.abort.signal });
        this.root.addEventListener('mouseleave', () => this.start(), { signal: this.abort.signal });
      }
      this.root.addEventListener('focusin', () => this.stop(), { signal: this.abort.signal });
      this.root.addEventListener('focusout', () => this.start(), { signal: this.abort.signal });
      document.addEventListener('visibilitychange', () => document.hidden ? this.stop() : this.start(), { signal: this.abort.signal });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          this.visible ? this.start() : this.stop();
        }, { threshold: 0.15 });
        this.observer.observe(this.root);
      }
      this.start();
    }

    normalize(next) {
      if (this.loop) return (next + this.slides.length) % this.slides.length;
      return Math.max(0, Math.min(next, this.slides.length - 1));
    }

    go(next, direction = 'next', userInitiated = false) {
      next = this.normalize(next);
      if (next === this.index || this.locked) return;
      const previous = this.slides[this.index];
      const incoming = this.slides[next];
      this.locked = true;
      previous.classList.add('is-previous');
      previous.dataset.direction = direction;
      incoming.dataset.direction = direction;
      incoming.classList.add('is-active');
      incoming.setAttribute('aria-hidden', 'false');
      previous.classList.remove('is-active');
      previous.setAttribute('aria-hidden', 'true');
      this.dots[this.index]?.classList.remove('is-active');
      this.dots[this.index]?.setAttribute('aria-selected', 'false');
      this.dots[next]?.classList.add('is-active');
      this.dots[next]?.setAttribute('aria-selected', 'true');
      this.index = next;
      window.setTimeout(() => {
        previous.classList.remove('is-previous');
        delete previous.dataset.direction;
        delete incoming.dataset.direction;
        this.locked = false;
      }, reduceMotion() ? 30 : 1700);
      if (userInitiated) this.restart();
    }

    start() {
      if (!this.autoplay || !this.visible || this.timer) return;
      this.timer = window.setInterval(() => this.go(this.index + 1, 'next'), this.interval);
    }
    stop() { window.clearInterval(this.timer); this.timer = null; }
    restart() { this.stop(); this.start(); }

    selectBlock(blockId) {
      const target = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
      if (target >= 0) this.go(target, target < this.index ? 'prev' : 'next');
      this.stop();
    }

    destroy() {
      this.stop();
      this.observer?.disconnect();
      this.abort.abort();
    }
  }

  const init = (scope = document) => {
    const roots = [...scope.querySelectorAll(selector)];
    if (scope.matches?.(selector)) roots.unshift(scope);
    roots.forEach((root) => {
      instances.get(root.id)?.destroy();
      instances.set(root.id, new HeroSliderV2(root));
    });
  };

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const id = `CustomFullscreenHeroV2-${event.detail.sectionId}`;
    instances.get(id)?.destroy();
    instances.delete(id);
  });
  document.addEventListener('shopify:block:select', (event) => instances.get(`CustomFullscreenHeroV2-${event.detail.sectionId}`)?.selectBlock(event.detail.blockId));
  document.addEventListener('shopify:block:deselect', (event) => instances.get(`CustomFullscreenHeroV2-${event.detail.sectionId}`)?.start());

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
